import posthog from 'posthog-js';

const ANON_KEY = 'exis_anon';

type ClientConfig = {
  key: string;
  apiHost: string;
  uiHost: string;
};

let started = false;

function telemetryUrl(): string {
  if (typeof window === 'undefined') return '/api/telemetry';
  const host = window.location.hostname;
  if (host === 'exis.pro' || host === 'www.exis.pro') {
    return 'https://app.exis.pro/api/telemetry';
  }
  return '/api/telemetry';
}

function firstPartyHost(apiHost: string): string {
  if (typeof window === 'undefined') return apiHost;
  const host = window.location.hostname;
  if (host === 'exis.pro' || host === 'www.exis.pro') {
    return `https://app.exis.pro${apiHost}`;
  }
  return apiHost;
}

export function initClientTelemetry(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  void loadClient();
}

async function loadClient(): Promise<void> {
  const cfg = await fetchConfig();
  if (!cfg) return;
  const distinctID = anonId();
  const opts = {
    api_host: firstPartyHost(cfg.apiHost),
    ui_host: cfg.uiHost,
    defaults: '2026-05-30' as const,
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    capture_exceptions: true,
    disable_toolbar: true,
    bootstrap: distinctID ? { distinctID } : undefined,
  };
  posthog.init(cfg.key, opts);
}

async function fetchConfig(): Promise<ClientConfig | null> {
  const url = `${telemetryUrl()}/config`;
  try {
    const res = await fetch(url, {
      method: 'GET',
      credentials: url.startsWith('/') ? 'same-origin' : 'omit',
      headers: { Accept: 'application/json' },
    });
    if (res.status === 204) return null;
    if (!res.ok) {
      console.error('[exis] failed to load PostHog config:', res.status);
      return null;
    }
    const body = (await res.json()) as ClientConfig;
    if (!body.key || !body.apiHost) return null;
    return body;
  } catch (err) {
    console.error('[exis] failed to load PostHog config:', err);
    return null;
  }
}

export function capture(
  event: string,
  properties?: Record<string, unknown>,
): void {
  const url = telemetryUrl();
  const body = {
    event,
    properties: withSession(properties),
  };
  void fetch(url, {
    method: 'POST',
    credentials: url.startsWith('/') ? 'same-origin' : 'omit',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  }).catch((err: unknown) => {
    console.error('[exis] failed to capture', body.event, err);
  });
}

function withSession(
  properties?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  let sessionId: string | undefined;
  try {
    sessionId = posthog.get_session_id();
  } catch {
    return properties;
  }
  if (!sessionId) return properties;
  return { ...properties, $session_id: sessionId };
}

function anonId(): string | undefined {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

export function capturePageview(pathname: string, search = ''): void {
  posthog.capture('$pageview', {
    $pathname: pathname,
    name: pathname === '/' ? 'home' : pathname.replace(/^\/+/, '') || 'home',
    $current_url: `${window.location.origin}${pathname}${search}`,
  });
}
